/**
 * OPS-11: Hotel Pulse Gauge — Command Center Edition
 *
 * Primary operational health indicator — large circular gauge with
 * color-coded status, contextual alert/risk counts, and pressure summary.
 * The single most important visual on the dashboard.
 *
 * Visual features:
 *   - Status Halo: ambient glow reflecting pulse status
 *   - Animated status dot: speed varies by severity
 *   - Subtle rotating arc behind gauge ring
 *   - Status border emphasis for warning/critical
 */

import { memo } from 'react';
import type { PulseScoreCore, PulseStatus, PulseDimensions, OperationalAlert, GuestExperienceRisk, SlaRisk } from '../../services/controlTowerTypes';
import type { OperationalConfidence } from '../../../utils/computeOperationalConfidence';
import { InfoTooltip } from './InfoTooltip';
import { SLABreachDisplay } from './SLABreachDisplay';

// ─── Trend ───────────────────────────────────────────────────────────────────

export type PulseTrend = 'improving' | 'stable' | 'deteriorating';

export function computeTrend(current: number, previous: number | null): PulseTrend {
  if (previous === null) return 'stable';
  const diff = current - previous;
  if (diff >= 2) return 'improving';
  if (diff <= -2) return 'deteriorating';
  return 'stable';
}

const TREND_CONFIG: Record<PulseTrend, { arrow: string; color: string; label: string }> = {
  improving:     { arrow: '↑', color: 'text-emerald-400', label: 'Improving' },
  stable:        { arrow: '→', color: 'text-amber-400',   label: 'Stable' },
  deteriorating: { arrow: '↓', color: 'text-rose-400',    label: 'Deteriorating' },
};

// ─── Status Config ───────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<PulseStatus, { color: string; bg: string; ring: string; label: string; border: string }> = {
  excellent: { color: 'text-sky-400',    bg: 'bg-sky-950/60',     ring: 'stroke-sky-400',   label: 'Excellent', border: 'border-sky-500/40' },
  healthy:   { color: 'text-sky-400',    bg: 'bg-sky-950/60',     ring: 'stroke-sky-400',   label: 'Healthy',   border: 'border-sky-500/40' },
  warning:   { color: 'text-amber-400',  bg: 'bg-amber-500/10',   ring: 'stroke-amber-400', label: 'Warning',   border: 'border-amber-500/30' },
  critical:  { color: 'text-rose-400',   bg: 'bg-rose-500/10',    ring: 'stroke-rose-400',  label: 'Critical',  border: 'border-rose-500/30' },
};

// ─── Status Halo — ambient glow per status ───────────────────────────────────

type HaloStatus = 'healthy' | 'warning' | 'critical';

const STATUS_HALO: Record<HaloStatus, string> = {
  healthy:  'shadow-[0_0_60px_rgba(16,185,129,0.15)] border-emerald-500/30',
  warning:  'shadow-[0_0_60px_rgba(245,158,11,0.18)] border-amber-500/30',
  critical: 'shadow-[0_0_70px_rgba(244,63,94,0.25)] border-rose-500/40',
};

const STATUS_RING: Record<HaloStatus, string> = {
  healthy:  '',
  warning:  'ring-1 ring-amber-500/20',
  critical: 'ring-1 ring-rose-500/25',
};

// ─── Status Dot Animation — speed varies by severity ─────────────────────────

const DOT_ANIMATION: Record<HaloStatus, string> = {
  healthy:  'animate-pulse-slow',
  warning:  'animate-ping',
  critical: 'animate-pulse-fast',
};

const DOT_COLOR: Record<HaloStatus, string> = {
  healthy:  'bg-sky-400',
  warning:  'bg-amber-400',
  critical: 'bg-rose-400',
};

// ─── Rotating Arc — stroke color per status ──────────────────────────────────

const ARC_STROKE: Record<HaloStatus, string> = {
  healthy:  'stroke-sky-400/[0.08]',
  warning:  'stroke-amber-400/[0.10]',
  critical: 'stroke-rose-400/[0.12]',
};

function toHaloStatus(status: PulseStatus): HaloStatus {
  if (status === 'critical') return 'critical';
  if (status === 'warning') return 'warning';
  return 'healthy';
}

function pressureLabel(dimensions: PulseDimensions): { label: string; color: string } {
  const vals = [dimensions.service, dimensions.operations, dimensions.infrastructure, dimensions.sustainability];
  const avg = vals.reduce((s, v) => s + v, 0) / vals.length;
  if (avg >= 80) return { label: 'Low', color: 'text-emerald-400' };
  if (avg >= 60) return { label: 'Moderate', color: 'text-amber-400' };
  if (avg >= 40) return { label: 'High', color: 'text-orange-400' };
  return { label: 'Critical', color: 'text-rose-400' };
}

// ─── Confidence Level Config ─────────────────────────────────────────────────

const CONFIDENCE_CONFIG: Record<OperationalConfidence['level'], { color: string; bg: string }> = {
  HIGH:    { color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  MEDIUM:  { color: 'text-sky-400',     bg: 'bg-sky-500/10' },
  FRAGILE: { color: 'text-amber-400',   bg: 'bg-amber-500/10' },
  LOW:     { color: 'text-rose-400',    bg: 'bg-rose-500/10' },
};

interface PulseGaugeProps {
  pulse: PulseScoreCore;
  alerts?: OperationalAlert[];
  risks?: GuestExperienceRisk[];
  previousScore?: number | null;
  confidence?: OperationalConfidence | null;
  slaRisk: SlaRisk;
}

export const PulseGauge = memo(function PulseGauge({ pulse, alerts = [], risks = [], previousScore = null, confidence = null, slaRisk }: PulseGaugeProps) {
  const config = STATUS_CONFIG[pulse.status];
  const haloStatus = toHaloStatus(pulse.status);
  const circumference = 2 * Math.PI * 54;
  const progress = (pulse.score / 100) * circumference;
  const offset = circumference - progress;
  const pressure = pressureLabel(pulse.dimensions);
  const criticalAlerts = alerts.filter((a) => a.severity === 'critical').length;
  const trend = computeTrend(pulse.score, previousScore);
  const trendCfg = TREND_CONFIG[trend];

  return (
    <div className={`min-h-[320px] rounded-2xl ${config.bg} border ${config.border} p-6 flex flex-col items-center h-full ${STATUS_HALO[haloStatus]} ${STATUS_RING[haloStatus]} transition-shadow duration-1000`}>
      {/* Header with live status dot */}
      <div className="flex items-center gap-2 mb-4">
        <span className="relative flex h-2 w-2">
          <span className={`${DOT_ANIMATION[haloStatus]} absolute inline-flex h-full w-full rounded-full opacity-75 ${DOT_COLOR[haloStatus]}`} />
          <span className={`relative inline-flex rounded-full h-2 w-2 ${DOT_COLOR[haloStatus]}`} />
        </span>
        <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider">Hotel Pulse</h2>
        <InfoTooltip text="The Pulse Score represents the overall operational health of the hotel, combining service quality, infrastructure status, and guest satisfaction into a single real-time metric (0–100)." />
      </div>

      {/* Circular Gauge with rotating arc */}
      <div className="relative w-36 h-36 mb-3">
        {/* Subtle rotating arc — ambient motion layer */}
        <svg className="absolute inset-0 w-full h-full animate-spin-slow" viewBox="0 0 120 120">
          <circle
            cx="60" cy="60" r="54"
            fill="none"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray="40 300"
            className={ARC_STROKE[haloStatus]}
          />
        </svg>
        {/* Main gauge */}
        <svg className="w-full h-full -rotate-90 relative" viewBox="0 0 120 120">
          {/* Background ring */}
          <circle cx="60" cy="60" r="54" fill="none" stroke="currentColor" strokeWidth="8" className="text-slate-700/50" />
          {/* Progress ring */}
          <circle
            cx="60" cy="60" r="54"
            fill="none"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className={`${config.ring} transition-all duration-1000 ease-out`}
          />
        </svg>
        {/* Score text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="flex items-center gap-1">
            <span className={`text-4xl font-bold ${config.color}`}>{pulse.score}</span>
            <span className={`text-xl font-bold ${trendCfg.color}`}>{trendCfg.arrow}</span>
          </div>
          <span className={`text-xs font-medium opacity-80 ${trendCfg.color}`}>{trendCfg.label}</span>
        </div>
      </div>

      {/* Status badge */}
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${config.color} ${config.bg} border border-current/20 mb-4`}>
        {config.label}
      </span>

      {/* Situation Summary — key counts at a glance */}
      <div className="w-full grid grid-cols-3 gap-1.5 mb-4">
        <div className="flex flex-col items-center py-2 rounded-lg bg-slate-900/40">
          <span className={`text-lg font-bold ${alerts.length > 0 ? (criticalAlerts > 0 ? 'text-rose-400' : 'text-amber-400') : 'text-slate-500'}`}>
            {alerts.length}
          </span>
          <span className="text-[10px] text-slate-500 uppercase">Alerts</span>
        </div>
        <div className="flex flex-col items-center py-2 rounded-lg bg-slate-900/40">
          <span className={`text-lg font-bold ${risks.length > 0 ? 'text-orange-400' : 'text-slate-500'}`}>
            {risks.length}
          </span>
          <span className="text-[10px] text-slate-500 uppercase">Risks</span>
        </div>
        <div className="flex flex-col items-center py-2 rounded-lg bg-slate-900/40">
          <span className={`text-lg font-bold ${pressure.color}`}>{pressure.label}</span>
          <span className="text-[10px] text-slate-500 uppercase">Pressure</span>
        </div>
      </div>

      {/* Operational Confidence */}
      {confidence && confidence.level && confidence.score != null && (() => {
        const cc = CONFIDENCE_CONFIG[confidence.level];
        if (!cc) return null;
        return (
          <div className={`w-full flex items-center justify-between px-3 py-2 rounded-lg ${cc.bg}`}>
            <span className="text-[10px] text-slate-400 uppercase tracking-wider flex items-center gap-1">Confidence <InfoTooltip text="The confidence score estimates the stability and reliability of the current Pulse Score based on data freshness, coverage, and consistency." /></span>
            <div className="flex items-center gap-1.5">
              <span className={`text-sm font-bold tabular-nums ${cc.color}`}>{confidence.score}</span>
              <span className={`text-[10px] font-bold uppercase ${cc.color}`}>{confidence.level}</span>
            </div>
          </div>
        );
      })()}

      {/* UI-42: SLA Breach Visibility */}
      <SLABreachDisplay slaRisk={slaRisk} />
    </div>
  );
});
